/**
 * 🏢 CompanyListView — /suppliers (메뉴: 거래처)
 *
 * 큰 탭 2개: 거래처 / 담당자 (admin-draft 2026-07-13 설계).
 *   거래처 탭 — 역할 필터 전체/고객/매입처 + 우측 "거래처 추가"
 *              GET /api/admin/company
 *   담당자 탭 — 역할 필터 전체/고객 담당자/매입처 담당자 + 우측 "담당자 추가"
 *              GET /api/admin/company/contacts
 * 필터 상태는 탭별로 유지. 추가 버튼은 활성 탭 것만.
 *
 * 이번 라운드 범위: 목록 + 추가 모달(생성). 거래처 상세 모달은 다음 라운드.
 */

import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import { escapeHtml } from '../lib/cells';
import { normalizePhone } from '../lib/phone';

type Mode = 'company' | 'contact';
type RoleFilter = api.CompanyRoleFilter;     // all | customer | supplier

interface CompanyListData {
  mode: Mode;

  // 거래처 탭
  cRows:   api.Company[];
  cTotal:  number;
  cPage:   number;
  cRole:   RoleFilter;
  cSearch: string;

  // 담당자 탭
  kRows:   api.CompanyContact[];
  kTotal:  number;
  kPage:   number;
  kRole:   RoleFilter;
  kSearch: string;

  // 공통
  loading:      boolean;
  loadingMore:  boolean;
  errorMessage: string;

  // 모달
  modal: null | 'add-company' | 'add-contact' | 'detail' | 'memo' | 'contact-detail';
}

// 국가 셀렉트 — 대표 국가 우선(추후 확장). 빈 값 = 미지정.
const COUNTRY_OPTIONS = ['KR', 'US', 'CN', 'HK', 'SG', 'UK', 'JP', 'TW', 'DE', 'VN'];

// 매입처 소싱 프로필 옵션 (admin-draft core-data.js 기준).
const VENDOR_OPTIONS    = ['HPE', 'DELL', 'INTEL', 'MICRON', 'SAMSUNG', 'NVIDIA', 'LENOVO', 'CISCO'];
const CATEGORY_OPTIONS  = ['Chassis', 'CPU', 'GPU', 'Memory', 'SSD', 'HDD'];
const CONDITION_OPTIONS = ['F/S', 'F/S Spare', 'NOB', 'Newbulk', 'Newpull', 'Bulk', 'REF'];

/** 한국 사업자등록번호 체크섬 검증(10자리). 하이픈 무관. 빈 값은 호출 전 처리. */
function isValidBRN(raw: string): boolean {
  const d = raw.replace(/[^0-9]/g, '');
  if (d.length !== 10) return false;
  const key = [1, 3, 7, 1, 3, 7, 1, 3, 5];
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(d[i]) * key[i];
  sum += Math.floor((Number(d[8]) * 5) / 10);
  const check = (10 - (sum % 10)) % 10;
  return check === Number(d[9]);
}

/** 한국 전화/휴대폰 형식 검증. 하이픈 무관, 0으로 시작·9~11자리. 빈 값은 호출 전 처리. */
function isValidKoreanPhone(raw: string): boolean {
  const d = raw.replace(/[^0-9]/g, '');
  if (!/^0\d{8,10}$/.test(d)) return false;
  // 휴대폰(01x): 10~11자리, 지역/대표번호(02·0xx): 9~11자리
  if (d.startsWith('01')) return d.length === 10 || d.length === 11;
  if (d.startsWith('02')) return d.length === 9 || d.length === 10;
  return d.length === 10 || d.length === 11;
}

/** 이메일 형식 검증(간단). 빈 값은 호출 전 처리. */
function isValidEmail(raw: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw);
}

/** 서버 에러 → 사용자 친화 메시지. zod/HTTP 원시 JSON 이 그대로 노출되지 않도록. */
function friendlyMessage(err: unknown): string {
  const raw = ((err as Error)?.message ?? '').trim();
  const body = raw.replace(/^HTTP \d+\s*/, '').trim();   // "HTTP 409 " 접두 제거
  if (/HTTP 5\d\d/.test(raw)) return '서버 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
  if (!body) return '요청을 처리하지 못했습니다.';
  // 배열/객체·zod 원시 형식이면 일반 메시지로 대체
  if (/^[[{]/.test(body) || /invalid_format|"code":|"path":/.test(body)) {
    return '입력값 형식을 확인해 주세요.';
  }
  return body;
}

/** Daum(카카오) 우편번호 서비스 — 무료·키 불필요. 필요 시 1회만 스크립트 로드. */
let _daumLoad: Promise<void> | null = null;
function loadDaumPostcode(): Promise<void> {
  const w = window as unknown as { daum?: { Postcode?: unknown } };
  if (w.daum?.Postcode) return Promise.resolve();
  if (_daumLoad) return _daumLoad;
  _daumLoad = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    s.onload = () => resolve();
    s.onerror = () => { _daumLoad = null; reject(new Error('주소 검색 스크립트를 불러오지 못했습니다.')); };
    document.head.appendChild(s);
  });
  return _daumLoad;
}

const COMPANY_ROLE_TABS: { key: RoleFilter; label: string }[] = [
  { key: 'all',      label: '전체' },
  { key: 'customer', label: '고객' },
  { key: 'supplier', label: '매입처' },
];
const CONTACT_ROLE_TABS: { key: RoleFilter; label: string }[] = [
  { key: 'all',      label: '전체' },
  { key: 'customer', label: '고객 담당자' },
  { key: 'supplier', label: '매입처 담당자' },
];

function companyRoleBadge(role: api.Company['role']): string {
  const style =
    role === '고객·매입처' ? 'background:#ecfdf5;color:#059669;border:1px solid #a7f3d0'
    : role === '매입처'    ? 'background:#fffbeb;color:#b45309;border:1px solid #fde68a'
    :                        'background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe';
  return `<span class="chip" style="${style}">${escapeHtml(role)}</span>`;
}
function contactRoleBadge(role: api.CompanyContact['role']): string {
  if (role === '-') return '<span class="text-muted">-</span>';
  const style =
    role === '고객·매입처 담당자' ? 'background:#ecfdf5;color:#059669;border:1px solid #a7f3d0'
    : role === '매입처 담당자'    ? 'background:#fffbeb;color:#b45309;border:1px solid #fde68a'
    :                              'background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe';
  return `<span class="chip" style="${style}">${escapeHtml(role)}</span>`;
}

export class CompanyListView extends SolaView<CompanyListData> {
  override events = {
    // ⚠ Sola first-match-wins(맵 순서). 모달 내부 액션은 반드시 @modal-backdrop
    //   (조상 요소)보다 먼저 — 안 그러면 backdrop 이 가로채 저장 등이 죽음.
    click: {
      '@mode-tab':       'handleModeTab',
      '@role-filter':    'handleRoleFilter',
      '@add-btn':        'handleAddClick',
      '@memo-edit':         'handleMemoEdit',
      '@contact-memo-edit': 'handleContactMemoEdit',
      '@company-row':    'handleCompanyRowClick',
      '@contact-row':    'handleContactRowClick',
      '@company-submit': 'handleCompanySubmit',
      '@contact-submit': 'handleContactSubmit',
      '@memo-submit':    'handleMemoSubmit',
      '@detail-edit':    'handleDetailEdit',
      '@company-delete': 'handleCompanyDelete',
      '@contact-delete': 'handleContactDelete',
      '@detail-cancel':  'handleDetailCancel',
      '@comp-pick':      'handleCompanyPick',
      '@ms-toggle':      'handleMsToggle',
      '@addr-search':    'handleAddrSearch',
      '@modal-close':    'handleModalClose',
      '@modal-backdrop': 'handleModalClose',   // 반드시 마지막(조상)
    },
    change: {
      '@ms-check': 'handleMsChange',
    },
    input: {
      '@search-input': 'handleSearchInput',
      '@comp-search':  'handleCompanySearch',
      '@contact-phone-input': 'handleContactPhoneInput',
    },
    keydown: {
      '@search-input': 'handleSearchKey',
    },
  };

  private _searchTimer: number | undefined;
  private _compTimer:   number | undefined;
  private _pickedCompany: { id: string; name: string } | null = null;
  private _companyEdit: api.CompanyDetail | null = null;   // 상세/수정 대상(생성이면 null)
  private _detailEditing = false;                          // 상세 모달 편집 모드(거래처·담당자 공용)
  private _memoTarget: { id: string; name: string; notes: string; kind: 'company' | 'contact' } | null = null;
  private _contactEdit: api.CompanyContact | null = null;  // 담당자 상세/수정 대상(생성이면 null)

  constructor() {
    super();
    this.data = {
      mode: 'company',
      cRows: [], cTotal: 0, cPage: 1, cRole: 'all', cSearch: '',
      kRows: [], kTotal: 0, kPage: 1, kRole: 'all', kSearch: '',
      loading: true, loadingMore: false, errorMessage: '',
      modal: null,
    };
  }

  override getTitle(): string { return '거래처 — HCLTech'; }

  override onMount(): void {
    void this.loadRows();
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }
  override onUnmount(): void {
    window.removeEventListener('scroll', this._onScroll);
    if (this._searchTimer) window.clearTimeout(this._searchTimer);
    if (this._compTimer)   window.clearTimeout(this._compTimer);
    super.onUnmount();
  }

  private _onScroll = (): void => {
    if (this.data.loading || this.data.loadingMore || this.data.modal) return;
    const shown = this.data.mode === 'company' ? this.data.cRows.length : this.data.kRows.length;
    const total = this.data.mode === 'company' ? this.data.cTotal : this.data.kTotal;
    if (shown >= total) return;
    const nearBottom = window.innerHeight + window.scrollY
      >= document.documentElement.scrollHeight - 400;
    if (nearBottom) void this.loadMore();
  };

  // ─── 로드 ──────────────────────────────────────────────────────────
  private async loadRows(): Promise<void> {
    this.data.loading = true;
    this.setBanner('loading', true);
    try {
      if (this.data.mode === 'company') {
        this.data.cPage = 1;
        const r = await api.fetchCompanyList({
          role: this.data.cRole, sort: 'name', order: 'asc',
          search: this.data.cSearch || undefined, page: 1, pageSize: 50,
        });
        this.data.cRows = r.rows; this.data.cTotal = r.total;
      } else {
        this.data.kPage = 1;
        const r = await api.fetchCompanyContacts({
          role: this.data.kRole, search: this.data.kSearch || undefined,
          page: 1, pageSize: 50,
        });
        this.data.kRows = r.rows; this.data.kTotal = r.total;
      }
      this.data.errorMessage = '';
    } catch (err) {
      this.data.errorMessage = `목록 로드 실패: ${(err as Error).message}`;
    } finally {
      this.data.loading = false;
      this.rerenderList();
    }
  }

  private async loadMore(): Promise<void> {
    if (this.data.loadingMore) return;
    this.data.loadingMore = true;
    this.rerenderFooter();
    try {
      if (this.data.mode === 'company') {
        const next = this.data.cPage + 1;
        const r = await api.fetchCompanyList({
          role: this.data.cRole, sort: 'name', order: 'asc',
          search: this.data.cSearch || undefined, page: next, pageSize: 50,
        });
        this.data.cRows = [...this.data.cRows, ...r.rows];
        this.data.cTotal = r.total; this.data.cPage = next;
      } else {
        const next = this.data.kPage + 1;
        const r = await api.fetchCompanyContacts({
          role: this.data.kRole, search: this.data.kSearch || undefined,
          page: next, pageSize: 50,
        });
        this.data.kRows = [...this.data.kRows, ...r.rows];
        this.data.kTotal = r.total; this.data.kPage = next;
      }
    } catch { /* 유지 */ }
    this.data.loadingMore = false;
    this.rerenderList();
  }

  // ─── 이벤트 ────────────────────────────────────────────────────────
  handleModeTab(_e: Event, target: HTMLElement): void {
    const mode = target.getAttribute('data-mode') as Mode | null;
    if (!mode || mode === this.data.mode) return;
    this.data.mode = mode;
    this.rerenderModeTabs();
    this.rerenderToolbar();   // 모드에 맞는 역할탭/검색 placeholder/추가버튼 갱신
    void this.loadRows();
  }

  handleRoleFilter(_e: Event, target: HTMLElement): void {
    const role = target.getAttribute('data-role') as RoleFilter | null;
    if (!role) return;
    if (this.data.mode === 'company') {
      if (role === this.data.cRole) return;
      this.data.cRole = role;
    } else {
      if (role === this.data.kRole) return;
      this.data.kRole = role;
    }
    this.rerenderToolbar();   // 역할탭 active 갱신(리스트는 loadRows 가 갱신)
    void this.loadRows();
  }

  handleSearchInput(_e: Event, target: HTMLElement): void {
    const v = (target as HTMLInputElement).value;
    if (this.data.mode === 'company') this.data.cSearch = v; else this.data.kSearch = v;
    if (this._searchTimer) window.clearTimeout(this._searchTimer);
    this._searchTimer = window.setTimeout(() => { void this.loadRows(); }, 300);
  }
  handleSearchKey(e: Event, _t: HTMLElement): void {
    if ((e as KeyboardEvent).key === 'Enter') {
      if (this._searchTimer) window.clearTimeout(this._searchTimer);
      void this.loadRows();
    }
  }

  // ─── 추가 모달 ─────────────────────────────────────────────────────
  handleAddClick(): void {
    this._pickedCompany = null;
    this._companyEdit = null;
    this._contactEdit = null;
    this._detailEditing = false;
    this.data.modal = this.data.mode === 'company' ? 'add-company' : 'add-contact';
    this.rerenderModal();
  }

  // 행 클릭 → 읽기전용 상세(폼) 모달
  async handleCompanyRowClick(_e: Event, target: HTMLElement): Promise<void> {
    const id = target.closest('[data-company-id]')?.getAttribute('data-company-id');
    if (!id) return;
    this._companyEdit = null;
    this._detailEditing = false;
    this.data.modal = 'detail';
    this.rerenderModal();   // 로딩 표시
    try {
      this._companyEdit = await api.fetchCompanyDetail(id);
    } catch (err) {
      this.rerenderModal();
      const box = this._rootElement?.querySelector<HTMLElement>('.company-detail-error');
      if (box) box.textContent = friendlyMessage(err);
      return;
    }
    if (this.data.modal === 'detail') this.rerenderModal();
  }

  // 상세 → 수정 전환(필드 활성화)
  handleDetailEdit(): void {
    this._detailEditing = true;
    this.rerenderModal();
  }
  // 수정 → 상세로 되돌리기(변경 취소)
  handleDetailCancel(): void {
    this._detailEditing = false;
    this.rerenderModal();
  }

  // 거래처 삭제 — 연결 딜은 미지정, 소속 담당자도 비활성(백엔드 처리).
  async handleCompanyDelete(): Promise<void> {
    const c = this._companyEdit;
    if (!c) return;
    if (!confirm(`거래처 "${c.name}"을(를) 삭제할까요?\n연결된 딜은 거래처 미지정으로 바뀌고, 소속 담당자도 함께 삭제됩니다.`)) return;
    try {
      await api.deactivateCompany(c.id);
      this._detailEditing = false;
      this._companyEdit = null;
      this.data.modal = null;
      this.rerenderModal();
      await this.loadRows();
    } catch (err) {
      const box = this._rootElement?.querySelector<HTMLElement>('.company-detail-error');
      if (box) box.textContent = err instanceof Error ? err.message : '삭제 실패';
    }
  }

  // 담당자 삭제 — 이 담당자를 쓰던 딜은 담당자 미지정(백엔드 처리).
  async handleContactDelete(): Promise<void> {
    const k = this._contactEdit;
    if (!k) return;
    if (!confirm(`담당자 "${k.name}"을(를) 삭제할까요?\n이 담당자를 쓰던 딜은 담당자 미지정으로 바뀝니다.`)) return;
    try {
      await api.deactivateCompanyContact(k.id);
      this._detailEditing = false;
      this._contactEdit = null;
      this.data.modal = null;
      this.rerenderModal();
      await this.loadRows();
    } catch (err) {
      const box = this._rootElement?.querySelector<HTMLElement>('.company-detail-error');
      if (box) box.textContent = err instanceof Error ? err.message : '삭제 실패';
    }
  }

  // 메모 연필 → 메모 편집 팝업 (거래처)
  handleMemoEdit(_e: Event, target: HTMLElement): void {
    const id = target.getAttribute('data-company-id');
    if (!id) return;
    const row = this.data.cRows.find((c) => c.id === id);
    if (!row) return;
    this._memoTarget = { id, name: row.name, notes: row.notes ?? '', kind: 'company' };
    this.data.modal = 'memo';
    this.rerenderModal();
  }
  // 메모 연필 → 메모 편집 팝업 (담당자)
  handleContactMemoEdit(_e: Event, target: HTMLElement): void {
    const id = target.getAttribute('data-contact-id');
    if (!id) return;
    const row = this.data.kRows.find((k) => k.id === id);
    if (!row) return;
    this._memoTarget = { id, name: row.name, notes: row.notes ?? '', kind: 'contact' };
    this.data.modal = 'memo';
    this.rerenderModal();
  }
  async handleMemoSubmit(): Promise<void> {
    const root = this.modalRoot();
    if (!root || !this._memoTarget) return;
    const memo = this.fieldVal(root, 'memo').trim();
    const { id, kind } = this._memoTarget;
    this.setModalBusy(root, true);
    try {
      if (kind === 'contact') await api.updateCompanyContactMemo(id, memo);
      else                    await api.updateCompanyMemo(id, memo);
      this.data.modal = null;
      this._memoTarget = null;
      this.rerenderModal();
      await this.loadRows();
    } catch (err) {
      this.setModalError(root, friendlyMessage(err));
      this.setModalBusy(root, false);
    }
  }

  // 담당자 행 클릭 → 읽기전용 상세(폼)
  handleContactRowClick(_e: Event, target: HTMLElement): void {
    const id = target.closest('[data-contact-id]')?.getAttribute('data-contact-id');
    if (!id) return;
    const row = this.data.kRows.find((k) => k.id === id);
    if (!row) return;
    this._contactEdit = row;
    this._detailEditing = false;
    // 소속 거래처 프리필(수정 전환 시 검색 결과 없이도 유지)
    this._pickedCompany = row.customer_id
      ? { id: row.customer_id, name: row.company_name ?? '' }
      : null;
    this.data.modal = 'contact-detail';
    this.rerenderModal();
  }

  handleModalClose(e: Event, target: HTMLElement): void {
    // 백드롭/닫기 버튼만 (모달 내부 클릭은 무시)
    if (target.getAttribute('data-action') === 'modal-backdrop' && e.target !== target) return;
    this.data.modal = null;
    this._pickedCompany = null;
    this._companyEdit = null;
    this._contactEdit = null;
    this._detailEditing = false;
    this._memoTarget = null;
    this.rerenderModal();
  }

  async handleCompanySubmit(): Promise<void> {
    const root = this.modalRoot();
    if (!root) return;
    const name = this.fieldVal(root, 'name').trim();
    if (!name) { this.setModalError(root, '거래처명을 입력하세요.'); return; }
    const isCustomer = root.querySelector<HTMLInputElement>('input[data-field="is_customer"]')?.checked ?? false;
    const isSupplier = root.querySelector<HTMLInputElement>('input[data-field="is_supplier"]')?.checked ?? false;
    if (!isCustomer && !isSupplier) { this.setModalError(root, '역할(고객/매입처)을 하나 이상 선택하세요.'); return; }
    const bizNo = this.fieldVal(root, 'business_no').trim();
    if (bizNo && !isValidBRN(bizNo)) {
      this.setModalError(root, '사업자등록번호가 올바르지 않습니다. (10자리 체크섬 불일치)');
      return;
    }
    const payload = {
      name,
      is_customer:         isCustomer,
      is_supplier:         isSupplier,
      representative_name: this.fieldVal(root, 'representative_name').trim() || null,
      business_no:         bizNo || null,
      address:             this.fieldVal(root, 'address').trim() || null,
      country:             this.fieldVal(root, 'country').trim() || null,
      notes:               this.fieldVal(root, 'notes').trim() || null,
      vendors:             this.msValues(root, 'vendors'),
      categories:          this.msValues(root, 'categories'),
      conditions:          this.msValues(root, 'conditions'),
    };
    this.setModalBusy(root, true);
    try {
      if (this._companyEdit) {
        await api.updateCompany(this._companyEdit.id, payload);
      } else {
        await api.createCompany(payload);
      }
      this.data.modal = null;
      this._companyEdit = null;
      this._detailEditing = false;
      this.rerenderModal();
      this.data.mode = 'company';
      await this.loadRows();
    } catch (err) {
      this.setModalError(root, friendlyMessage(err));
      this.setModalBusy(root, false);
    }
  }

  handleCompanySearch(_e: Event, target: HTMLElement): void {
    const q = (target as HTMLInputElement).value.trim();
    this._pickedCompany = null;
    if (this._compTimer) window.clearTimeout(this._compTimer);
    if (!q) { this.renderCompResults([]); return; }
    this._compTimer = window.setTimeout(async () => {
      try {
        const r = await api.fetchCompanyList({ search: q, sort: 'name', order: 'asc', page: 1, pageSize: 8 });
        this.renderCompResults(r.rows);
      } catch { this.renderCompResults([]); }
    }, 250);
  }
  handleCompanyPick(_e: Event, target: HTMLElement): void {
    const id = target.getAttribute('data-company-id');
    const name = target.getAttribute('data-company-name');
    if (!id || !name) return;
    this._pickedCompany = { id, name };
    const root = this.modalRoot();
    if (root) {
      const input = root.querySelector<HTMLInputElement>('input[data-action="comp-search"]');
      if (input) input.value = name;
      this.renderCompResults([]);
    }
  }

  /** 전화번호(휴대) 입력 — 하이픈 차단, 숫자만 허용(저장 시 서버가 하이픈 삽입). */
  handleContactPhoneInput(_e: Event, target: HTMLElement): void {
    const el = target as HTMLInputElement;
    el.value = el.value.replace(/[^0-9]/g, '');
  }

  async handleContactSubmit(): Promise<void> {
    const root = this.modalRoot();
    if (!root) return;
    if (!this._pickedCompany) { this.setModalError(root, '소속 거래처를 선택하세요.'); return; }
    const name = this.fieldVal(root, 'name').trim();
    if (!name) { this.setModalError(root, '담당자 이름을 입력하세요.'); return; }
    const email       = this.fieldVal(root, 'email').trim();
    const phone       = this.fieldVal(root, 'phone').trim();
    const officePhone = this.fieldVal(root, 'office_phone').trim();
    if (email && !isValidEmail(email)) { this.setModalError(root, '이메일 형식이 올바르지 않습니다.'); return; }
    if (phone && !isValidKoreanPhone(phone)) { this.setModalError(root, '전화번호 형식이 올바르지 않습니다.'); return; }
    if (officePhone && !isValidKoreanPhone(officePhone)) { this.setModalError(root, '사무실번호 형식이 올바르지 않습니다.'); return; }
    const payload = {
      customer_id:  Number(this._pickedCompany.id),
      name,
      position:     this.fieldVal(root, 'position').trim() || null,
      affiliation:  this.fieldVal(root, 'affiliation').trim() || null,
      email:        email || null,
      phone:        phone || null,
      office_phone: officePhone || null,
      memo:         this.fieldVal(root, 'memo').trim() || null,
    };
    this.setModalBusy(root, true);
    try {
      if (this._contactEdit) {
        await api.updateCompanyContact(this._contactEdit.id, payload);
      } else {
        await api.createCompanyContact(payload);
      }
      this.data.modal = null;
      this._pickedCompany = null;
      this._contactEdit = null;
      this._detailEditing = false;
      this.rerenderModal();
      this.data.mode = 'contact';
      await this.loadRows();
    } catch (err) {
      this.setModalError(root, friendlyMessage(err));
      this.setModalBusy(root, false);
    }
  }

  // ─── 렌더 ──────────────────────────────────────────────────────────
  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-company-list">
        <header class="page-header">
          <div>
            <h1>🏢 거래처</h1>
            <p class="page-subtitle">고객·매입처 거래처와 담당자를 통합 관리합니다.</p>
          </div>
        </header>

        <div class="company-mode-tabs">${this.renderModeTabs()}</div>

        <div class="company-main">
          ${this.renderToolbar()}
          <div class="company-list-region">${this.renderListRegion()}</div>
        </div>

        <div class="company-modal-root">${this.renderModal()}</div>
      </div>
    `;
  }

  private renderModeTabs(): string {
    const tab = (m: Mode, label: string) =>
      `<button class="company-mode-tab${this.data.mode === m ? ' is-on' : ''}" type="button"
               data-action="mode-tab" data-mode="${m}"
               aria-pressed="${this.data.mode === m ? 'true' : 'false'}">${label}</button>`;
    return tab('company', '거래처') + tab('contact', '담당자');
  }

  // 툴바(검색 input 포함)와 리스트 영역을 분리 — 자동검색 시 리스트만 갱신해
  //   검색 input 을 재렌더하지 않는다(포커스/커서 유지).
  private renderToolbar(): string {
    const tabs   = this.data.mode === 'company' ? COMPANY_ROLE_TABS : CONTACT_ROLE_TABS;
    const role   = this.data.mode === 'company' ? this.data.cRole : this.data.kRole;
    const search = this.data.mode === 'company' ? this.data.cSearch : this.data.kSearch;
    const addLabel = this.data.mode === 'company' ? '거래처 추가' : '담당자 추가';
    const placeholder = this.data.mode === 'company'
      ? '검색 : 회사명, 대표자, 사업자번호, 국가, 메모'
      : '검색 : 담당자명, 이메일, 전화, 직책, 소속 거래처';
    const roleSeg = tabs.map((t) =>
      `<button class="company-role-tab${role === t.key ? ' is-on' : ''}" type="button"
               data-action="role-filter" data-role="${t.key}"
               aria-pressed="${role === t.key ? 'true' : 'false'}">${t.label}</button>`
    ).join('');
    return `
      <div class="company-toolbar">
        <div class="company-role-tabs">${roleSeg}</div>
        <input class="filter-search" type="search" placeholder="${placeholder}"
               data-action="search-input" value="${escapeHtml(search)}" />
        <div class="company-toolbar-spacer"></div>
        <button class="btn btn-primary company-add-btn" type="button" data-action="add-btn">${addLabel}</button>
      </div>`;
  }

  private renderListRegion(): string {
    return `
      <div class="banner-loading${this.data.loading ? '' : ' hidden'}">목록 불러오는 중…</div>
      <div class="banner-error${this.data.errorMessage ? '' : ' hidden'}">${escapeHtml(this.data.errorMessage)}</div>

      <section class="grid-wrap">${this.renderTable()}</section>

      <p class="list-footer text-muted">${this.footerText()}</p>
    `;
  }

  private renderTable(): string {
    return this.data.mode === 'company' ? this.renderCompanyTable() : this.renderContactTable();
  }

  private renderCompanyTable(): string {
    if (this.data.cRows.length === 0 && !this.data.loading) {
      return `<p class="empty">조건에 맞는 거래처가 없습니다.</p>`;
    }
    const body = this.data.cRows.map((c) => `
      <tr class="company-row company-row-click${c.is_active ? '' : ' company-row-inactive'}" data-action="company-row" data-company-id="${escapeHtml(c.id)}">
        <td><div class="company-row-name">${escapeHtml(c.name)}${c.is_active ? '' : ' <span class="text-muted">(비활성)</span>'}</div></td>
        <td>${companyRoleBadge(c.role)}</td>
        <td>${c.primary_contact_name ? escapeHtml(c.primary_contact_name) : '<span class="text-muted">-</span>'}${c.contact_count > 1 ? ` <span class="text-muted">외 ${c.contact_count - 1}</span>` : ''}</td>
        <td>${c.country ? escapeHtml(c.country) : '<span class="text-muted">-</span>'}</td>
        <td class="company-memo-cell">
          <button type="button" class="company-memo-btn${c.notes ? ' has-memo' : ''}" data-action="memo-edit"
                  data-company-id="${escapeHtml(c.id)}" title="${c.notes ? escapeHtml(c.notes) : '메모 추가'}" aria-label="메모">✎</button>
        </td>
      </tr>`).join('');
    return `
      <table class="grid-table company-list-table">
        <thead><tr>
          <th>거래처명</th><th>역할</th><th>담당자</th><th>국가</th><th class="company-memo-col">메모</th>
        </tr></thead>
        <tbody>${body}</tbody>
      </table>`;
  }

  private renderContactTable(): string {
    if (this.data.kRows.length === 0 && !this.data.loading) {
      return `<p class="empty">조건에 맞는 담당자가 없습니다.</p>`;
    }
    const dash = '<span class="text-muted">-</span>';
    const body = this.data.kRows.map((k) => `
      <tr class="company-row company-row-click" data-action="contact-row" data-contact-id="${escapeHtml(k.id)}">
        <td><div class="company-row-name">${escapeHtml(k.name)}</div></td>
        <td>${contactRoleBadge(k.role)}</td>
        <td>${k.company_name ? escapeHtml(k.company_name) : dash}</td>
        <td>${k.position ? escapeHtml(k.position) : dash}</td>
        <td>${k.affiliation ? escapeHtml(k.affiliation) : dash}</td>
        <td>${k.email ? escapeHtml(k.email) : dash}</td>
        <td>${k.phone ? escapeHtml(normalizePhone(k.phone) ?? k.phone) : dash}</td>
        <td>${k.office_phone ? escapeHtml(k.office_phone) : dash}</td>
        <td class="company-memo-cell">
          <button type="button" class="company-memo-btn${k.notes ? ' has-memo' : ''}" data-action="contact-memo-edit"
                  data-contact-id="${escapeHtml(k.id)}" title="${k.notes ? escapeHtml(k.notes) : '메모 추가'}" aria-label="메모">✎</button>
        </td>
      </tr>`).join('');
    return `
      <table class="grid-table company-list-table">
        <thead><tr>
          <th>담당자</th><th>역할</th><th>소속 거래처</th><th>직책</th><th>소속</th><th>이메일</th><th>전화번호</th><th>사무실번호</th><th class="company-memo-col">메모</th>
        </tr></thead>
        <tbody>${body}</tbody>
      </table>`;
  }

  private footerText(): string {
    const shown = this.data.mode === 'company' ? this.data.cRows.length : this.data.kRows.length;
    const total = this.data.mode === 'company' ? this.data.cTotal : this.data.kTotal;
    const unit  = this.data.mode === 'company' ? '곳' : '명';
    if (this.data.loadingMore) return `총 ${total} ${unit} · 불러오는 중…`;
    if (shown < total) return `총 ${total} ${unit} · ${shown} 표시 (스크롤해 더 보기)`;
    return `총 ${total} ${unit}`;
  }

  // ─── 모달 렌더 ─────────────────────────────────────────────────────
  private renderModal(): string {
    if (this.data.modal === 'add-company')     return this.renderCompanyModal('create');
    if (this.data.modal === 'detail')          return this.renderCompanyModal(this._detailEditing ? 'edit' : 'view');
    if (this.data.modal === 'add-contact')     return this.renderContactModal('create');
    if (this.data.modal === 'contact-detail')  return this.renderContactModal(this._detailEditing ? 'edit' : 'view');
    if (this.data.modal === 'memo')            return this.renderMemoModal();
    return '';
  }

  private renderMemoModal(): string {
    const t = this._memoTarget;
    return `
      <div class="company-modal-backdrop" data-action="modal-backdrop">
        <div class="company-modal company-memo-modal" role="dialog" aria-label="메모">
          <div class="company-modal-head">
            <h2>${t ? escapeHtml(t.name) : ''} 메모</h2>
            <button class="company-modal-x" type="button" data-action="modal-close" aria-label="닫기">✕</button>
          </div>
          <div class="company-modal-body">
            <textarea data-field="memo" rows="5" placeholder="메모를 입력하세요.">${escapeHtml(t?.notes ?? '')}</textarea>
            <p class="company-modal-error hidden"></p>
          </div>
          <div class="company-modal-foot">
            <button class="btn btn-primary" type="button" data-action="memo-submit">저장</button>
            <button class="btn btn-secondary" type="button" data-action="modal-close">취소</button>
          </div>
        </div>
      </div>`;
  }

  /**
   * 거래처 폼 모달 — 3 모드:
   *   create : 빈 폼(편집 가능), 거래처 추가, [저장 / 취소]
   *   view   : 상세 프리필(읽기전용), 거래처 상세, [수정 / 닫기]
   *   edit   : 상세 프리필(편집 가능), 거래처 수정, [저장 / 취소]
   */
  private renderCompanyModal(mode: 'create' | 'view' | 'edit'): string {
    const e = mode === 'create' ? null : this._companyEdit;
    const ro = mode === 'view';                        // 읽기전용 여부
    const dis = ro ? ' disabled' : '';
    const loading = mode !== 'create' && !this._companyEdit;
    const val = (v: string | null | undefined) => escapeHtml(v ?? '');
    const title = mode === 'create' ? '거래처 추가' : mode === 'edit' ? '거래처 수정' : '거래처 상세';
    const countrySel = (v: string | null | undefined) => {
      const custom = v && !COUNTRY_OPTIONS.includes(v)
        ? `<option value="${escapeHtml(v)}" selected>${escapeHtml(v)}</option>` : '';
      return `<option value=""${!v ? ' selected' : ''}>미지정</option>` + custom +
        COUNTRY_OPTIONS.map((c) => `<option value="${c}"${v === c ? ' selected' : ''}>${c}</option>`).join('');
    };

    const foot =
      mode === 'view'
        ? `<button class="btn btn-danger-outline company-detail-delete" type="button" data-action="company-delete">삭제</button>
           <button class="btn btn-primary" type="button" data-action="detail-edit">수정</button>
           <button class="btn btn-secondary" type="button" data-action="modal-close">닫기</button>`
        : mode === 'edit'
        ? `<button class="btn btn-primary" type="button" data-action="company-submit">저장</button>
           <button class="btn btn-secondary" type="button" data-action="detail-cancel">취소</button>`
        : `<button class="btn btn-primary" type="button" data-action="company-submit">저장</button>
           <button class="btn btn-secondary" type="button" data-action="modal-close">취소</button>`;

    const bodyInner = loading
      ? `<div class="company-detail-loading">불러오는 중…</div><p class="company-detail-error"></p>`
      : `
            <div class="company-form-grid">
              <div class="company-field">
                <span>거래처 역할 <b class="req">*</b></span>
                <div class="company-check-row">
                  <label><input data-field="is_customer" type="checkbox"${!e || e.is_customer ? ' checked' : ''}${dis} /> 고객</label>
                  <label><input data-field="is_supplier" type="checkbox"${e && e.is_supplier ? ' checked' : ''}${dis} /> 매입처</label>
                </div>
              </div>
              <label class="company-field">
                <span>국가</span>
                <select data-field="country"${dis}>${countrySel(e?.country)}</select>
              </label>

              <label class="company-field">
                <span>거래처명 <b class="req">*</b></span>
                <input data-field="name" type="text" placeholder="회사명" value="${val(e?.name)}"${dis} />
              </label>
              <label class="company-field">
                <span>대표자명</span>
                <input data-field="representative_name" type="text" value="${val(e?.representative_name)}"${dis} />
              </label>

              <label class="company-field">
                <span>사업자등록번호</span>
                <input data-field="business_no" type="text" value="${val(e?.business_no)}"${dis} />
              </label>
              <div class="company-field">
                <span>사업자 등록 파일</span>
                <div class="company-file-stub" title="다음 라운드에 지원">미등록 <span class="text-muted">(추후 지원)</span></div>
              </div>

              <div class="company-field company-field-span2">
                <span>주소</span>
                <div class="company-addr-row">
                  <input data-field="address" type="text" placeholder="주소 검색으로 입력" value="${val(e?.address)}"${dis} />
                  ${ro ? '' : '<button type="button" class="btn btn-secondary company-addr-btn" data-action="addr-search">주소 검색</button>'}
                </div>
              </div>

              ${this.renderMultiSelect('vendors', '소싱 프로필 · 취급 벤더', VENDOR_OPTIONS, false, e?.vendors ?? [], ro)}
              ${this.renderMultiSelect('categories', '소싱 프로필 · 취급 카테고리', CATEGORY_OPTIONS, false, e?.categories ?? [], ro)}
              ${this.renderMultiSelect('conditions', '소싱 프로필 · 취급 컨디션', CONDITION_OPTIONS, true, e?.conditions ?? [], ro)}

              <label class="company-field company-field-span2">
                <span>메모</span>
                <textarea data-field="notes" rows="2"${dis}>${val(e?.notes)}</textarea>
              </label>
            </div>
            <p class="company-modal-error hidden"></p>`;

    return `
      <div class="company-modal-backdrop" data-action="modal-backdrop">
        <div class="company-modal company-modal-wide" role="dialog" aria-label="${title}">
          <div class="company-modal-head">
            <h2>${title}${e ? ` ${companyRoleBadge(e.role)}` : ''}</h2>
            <button class="company-modal-x" type="button" data-action="modal-close" aria-label="닫기">✕</button>
          </div>
          <div class="company-modal-body">${bodyInner}</div>
          <div class="company-modal-foot">${loading ? '<button class="btn btn-secondary" type="button" data-action="modal-close">닫기</button>' : foot}</div>
        </div>
      </div>`;
  }

  private renderMultiSelect(group: string, label: string, options: string[], full: boolean, selected: string[] = [], readonly = false): string {
    const sel = new Set(selected);
    const dis = readonly ? ' disabled' : '';
    const opts = options.map((o) =>
      `<label class="company-ms-opt"><input type="checkbox" data-action="ms-check" data-ms="${group}" value="${escapeHtml(o)}"${sel.has(o) ? ' checked' : ''}${dis} /> ${escapeHtml(o)}</label>`
    ).join('');
    const summary = selected.length ? escapeHtml(selected.join(', ')) : '선택 없음';
    return `
      <div class="company-field company-ms${full ? ' company-field-span2' : ''}${selected.length ? ' has-sel' : ''}" data-ms-group="${group}">
        <span>${label}</span>
        <button type="button" class="company-ms-toggle" data-action="ms-toggle"${dis}>
          <span class="company-ms-summary">${summary}</span>
          <span class="company-ms-caret">▾</span>
        </button>
        <div class="company-ms-panel">${opts}</div>
      </div>`;
  }

  handleMsToggle(_e: Event, target: HTMLElement): void {
    const box = target.closest<HTMLElement>('.company-ms');
    if (!box) return;
    const open = box.classList.contains('is-open');
    // 다른 패널 닫기
    this.modalRoot()?.querySelectorAll('.company-ms.is-open').forEach((el) => el.classList.remove('is-open'));
    if (!open) box.classList.add('is-open');
  }

  handleMsChange(_e: Event, target: HTMLElement): void {
    const box = target.closest<HTMLElement>('.company-ms');
    if (!box) return;
    const checked = box.querySelectorAll<HTMLInputElement>('input[data-action="ms-check"]:checked');
    const summary = box.querySelector<HTMLElement>('.company-ms-summary');
    if (summary) {
      summary.textContent = checked.length === 0
        ? '선택 없음'
        : Array.from(checked).map((c) => c.value).join(', ');
    }
    box.classList.toggle('has-sel', checked.length > 0);
  }

  private msValues(root: HTMLElement, group: string): string[] {
    return Array.from(root.querySelectorAll<HTMLInputElement>(`input[data-ms="${group}"]:checked`)).map((i) => i.value);
  }

  async handleAddrSearch(): Promise<void> {
    const root = this.modalRoot();
    if (!root) return;
    const input = root.querySelector<HTMLInputElement>('[data-field="address"]');
    try {
      await loadDaumPostcode();
      const w = window as unknown as {
        daum: { Postcode: new (opts: { oncomplete: (d: { roadAddress?: string; jibunAddress?: string; address?: string }) => void }) => { open: () => void } };
      };
      new w.daum.Postcode({
        oncomplete: (d) => {
          if (input) { input.value = d.roadAddress || d.address || d.jibunAddress || ''; input.focus(); }
        },
      }).open();
    } catch (err) {
      this.setModalError(root, (err as Error).message);
    }
  }

  /**
   * 담당자 폼 모달 — 3 모드(거래처 모달과 동일 패턴):
   *   create : 빈 폼, 담당자 추가, [저장 / 취소], 소속=검색
   *   view   : 프리필 읽기전용, 담당자 상세, [수정 / 삭제 / 닫기], 소속=텍스트
   *   edit   : 프리필 편집, 담당자 수정, [저장 / 취소], 소속=검색(프리필)
   */
  private renderContactModal(mode: 'create' | 'view' | 'edit'): string {
    const k = mode === 'create' ? null : this._contactEdit;
    const ro = mode === 'view';
    const dis = ro ? ' disabled' : '';
    const val = (v: string | null | undefined) => escapeHtml(v ?? '');
    const title = mode === 'create' ? '담당자 추가' : mode === 'edit' ? '담당자 수정' : '담당자 상세';

    // 소속 거래처: view=텍스트, create/edit=검색(edit 은 현재값 프리필)
    const companyField = ro
      ? `<div class="company-field company-field-span2">
           <span>소속 거래처</span>
           <div class="company-file-stub">${k?.company_name ? escapeHtml(k.company_name) : '<span class="text-muted">-</span>'}</div>
         </div>`
      : `<label class="company-field company-field-search company-field-span2">
           <span>소속 거래처 <b class="req">*</b></span>
           <input data-action="comp-search" type="text" placeholder="거래처명으로 검색" autocomplete="off" value="${val(k?.company_name)}" />
           <div class="comp-results"></div>
         </label>`;

    const foot =
      mode === 'view'
        ? `<button class="btn btn-danger-outline company-detail-delete" type="button" data-action="contact-delete">삭제</button>
           <button class="btn btn-primary" type="button" data-action="detail-edit">수정</button>
           <button class="btn btn-secondary" type="button" data-action="modal-close">닫기</button>`
        : mode === 'edit'
        ? `<button class="btn btn-primary" type="button" data-action="contact-submit">저장</button>
           <button class="btn btn-secondary" type="button" data-action="detail-cancel">취소</button>`
        : `<button class="btn btn-primary" type="button" data-action="contact-submit">저장</button>
           <button class="btn btn-secondary" type="button" data-action="modal-close">취소</button>`;

    return `
      <div class="company-modal-backdrop" data-action="modal-backdrop">
        <div class="company-modal company-modal-wide" role="dialog" aria-label="${title}">
          <div class="company-modal-head">
            <h2>${title}${k ? ` ${contactRoleBadge(k.role)}` : ''}</h2>
            <button class="company-modal-x" type="button" data-action="modal-close" aria-label="닫기">✕</button>
          </div>
          <div class="company-modal-body">
            <div class="company-form-grid">
              ${companyField}

              <label class="company-field">
                <span>담당자 이름 <b class="req">*</b></span>
                <input data-field="name" type="text" value="${val(k?.name)}"${dis} />
              </label>
              <label class="company-field">
                <span>직책</span>
                <input data-field="position" type="text" value="${val(k?.position)}"${dis} />
              </label>

              <label class="company-field">
                <span>소속</span>
                <input data-field="affiliation" type="text" placeholder="부서/팀 등" value="${val(k?.affiliation)}"${dis} />
              </label>
              <label class="company-field">
                <span>이메일</span>
                <input data-field="email" type="email" value="${val(k?.email)}"${dis} />
              </label>

              <label class="company-field">
                <span>전화번호</span>
                <input data-field="phone" data-action="contact-phone-input" type="text" placeholder="010-1234-5678" value="${escapeHtml((k?.phone ?? '').replace(/[^0-9]/g, ''))}"${dis} />
              </label>
              <label class="company-field">
                <span>사무실번호</span>
                <input data-field="office_phone" type="text" placeholder="02-1234-5678 (하이픈 포함)" value="${val(k?.office_phone)}"${dis} />
              </label>

              <label class="company-field company-field-span2">
                <span>메모</span>
                <textarea data-field="memo" rows="2"${dis}>${val(k?.notes)}</textarea>
              </label>
            </div>
            <p class="company-modal-error hidden"></p>
          </div>
          <div class="company-modal-foot">${foot}</div>
        </div>
      </div>`;
  }

  private renderCompResults(rows: api.Company[]): void {
    const root = this.modalRoot();
    const box  = root?.querySelector<HTMLElement>('.comp-results');
    if (!box) return;
    if (rows.length === 0) { box.innerHTML = ''; box.classList.remove('is-open'); return; }
    box.innerHTML = rows.map((c) =>
      `<button type="button" class="comp-result" data-action="comp-pick"
               data-company-id="${escapeHtml(c.id)}" data-company-name="${escapeHtml(c.name)}">
         ${escapeHtml(c.name)} <span class="text-muted">${escapeHtml(c.role)}</span>
       </button>`).join('');
    box.classList.add('is-open');
  }

  // ─── 부분 갱신 ─────────────────────────────────────────────────────
  // 리스트 영역만 갱신 — 검색 input(툴바)은 건드리지 않아 포커스/커서 유지.
  private rerenderList(): void {
    const el = this._rootElement?.querySelector('.company-list-region');
    if (el) el.innerHTML = this.renderListRegion();
  }
  // 툴바(역할탭/검색/추가) 갱신 — 모드전환·역할필터 등 툴바 자체가 바뀔 때만.
  //   (검색 중이 아닐 때 호출 → 검색 input 재렌더 무해)
  private rerenderToolbar(): void {
    const el = this._rootElement?.querySelector('.company-toolbar');
    if (el) el.outerHTML = this.renderToolbar();
  }
  private rerenderModeTabs(): void {
    const el = this._rootElement?.querySelector('.company-mode-tabs');
    if (el) el.innerHTML = this.renderModeTabs();
  }
  private rerenderModal(): void {
    const el = this._rootElement?.querySelector('.company-modal-root');
    if (el) el.innerHTML = this.renderModal();
  }
  private rerenderFooter(): void {
    const el = this._rootElement?.querySelector<HTMLElement>('.list-footer');
    if (el) el.textContent = this.footerText();
  }

  // ─── 헬퍼 ──────────────────────────────────────────────────────────
  private modalRoot(): HTMLElement | null {
    return this._rootElement?.querySelector<HTMLElement>('.company-modal') ?? null;
  }
  private fieldVal(root: HTMLElement, field: string): string {
    return root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[data-field="${field}"]`)?.value ?? '';
  }
  private setModalError(root: HTMLElement, msg: string): void {
    const el = root.querySelector<HTMLElement>('.company-modal-error');
    if (el) { el.textContent = msg; el.classList.remove('hidden'); }
  }
  private setModalBusy(root: HTMLElement, busy: boolean): void {
    root.querySelectorAll<HTMLButtonElement>('.company-modal-foot button')
      .forEach((b) => { b.disabled = busy; });
  }
  private setBanner(kind: 'loading', on: boolean): void {
    const el = this._rootElement?.querySelector<HTMLElement>(`.banner-${kind}`);
    if (!el) return;
    if (on) el.classList.remove('hidden'); else el.classList.add('hidden');
  }
}
